import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { models } from "types/api/viewModels.ts";
import {
	useEmptyShoppingCartMutation,
	useGetShoppingCartQuery,
	useLoadCartTemplateMutation,
	useUpdateShoppingCartMutation,
	useValidateShoppingCartMutation
} from "features/MarketingMaterials/api/merchProductsApi.ts";
import { CartShippingDetailsState, marketingMaterialsCartActions } from "features/reducers/marketingMaterials/cart.ts";
import { RootState } from "stores/application.store";
import { CampaignAllocation } from "features/MarketingMaterials/api/campaignApi.ts";

export type MarketingMaterialsCart = {
	cartItems: models["ShoppingCartItem"][];
	campaignItems: CampaignAllocation[];
	cartSize: number;
};

const createValidationFallback = (): models["ShoppingCartValidation"] => ({
	isValid: false,
	errors: [
		{
			id: null,
			message: "Unable to validate shopping cart.",
			code: "Unknown"
		}
	],
	info: []
});

const useMarketingMaterialsCart = () => {
	const dispatch = useDispatch();
	const cartItems = useSelector((state: RootState) => state.marketingMaterialsCart.cartItems);
	const campaignItems = useSelector((state: RootState) => state.marketingMaterialsCart.campaignItems);
	const selectedCampaignKeys = useSelector((state: RootState) => state.marketingMaterialsCart.selectedCampaignKeys);
	const confirmedItems = useSelector((state: RootState) => state.marketingMaterialsCart.confirmedItems);
	const selectedMerchAccount = useSelector((state: RootState) => state.marketingMaterialsCart.selectedMerchAccount);
	const isMerchAccountSelectionError = useSelector(
		(state: RootState) => state.marketingMaterialsCart.isMerchAccountSelectionError
	);
	const shippingDetails = useSelector((state: RootState) => state.marketingMaterialsCart.shippingDetails);
	const validation = useSelector((state: RootState) => state.marketingMaterialsCart.validation);
	const validationErrors =
		validation?.errors?.map((error) => error.message ?? error.code ?? "Validation error") ?? [];
	const validationInfoMessages =
		validation?.info?.map((info) => info.message ?? info.code ?? "Validation note") ?? [];
	const { data, isLoading: isShoppingCartLoading } = useGetShoppingCartQuery();
	const [validateCart, { isLoading: isValidatingShoppingCart }] = useValidateShoppingCartMutation();
	const [updateCart] = useUpdateShoppingCartMutation();
	const [emptyCart] = useEmptyShoppingCartMutation();
	const [loadCartFromTemplate] = useLoadCartTemplateMutation();
	const cartSize = cartItems.reduce((total, item) => total + (item.quantityOrdered ?? 0), 0);

	useEffect(() => {
		dispatch(marketingMaterialsCartActions.setCartItems(data?.items ?? []));
	}, [data?.items, dispatch]);

	const loadCart = async (templateId: string) => {
		const newCart = await loadCartFromTemplate({ templateId }).unwrap();
		dispatch(marketingMaterialsCartActions.setCartItems(newCart?.items ?? []));
	};

	const addCartItem = async (
		item: models["MerchandiseProductViewModel"],
		quantityChange: number,
		isOverride: boolean = false
	) => {
		if (cartSize === 0) {
			dispatch(marketingMaterialsCartActions.resetCart());
		}
		if (!item?.id || !item?.sku || !Number.isInteger(quantityChange) || (!isOverride && quantityChange === 0)) {
			return;
		}

		const previousCartItems = [...cartItems];
		const existingCartItem = cartItems.find((cartItem) => cartItem.id === item.id);
		const currentQuantity = existingCartItem?.quantityOrdered ?? 0;

		const nextQuantity = isOverride ? Math.max(quantityChange, 0) : Math.max(currentQuantity + quantityChange, 0);

		dispatch(marketingMaterialsCartActions.updateItemQuantity({ item, quantityChange: nextQuantity }));

		try {
			const updatedCart = await updateCart({ item, quantity: nextQuantity }).unwrap();
			dispatch(marketingMaterialsCartActions.setCartItems(updatedCart?.items ?? []));

			if (nextQuantity > currentQuantity) {
				dispatch(marketingMaterialsCartActions.showAddToCartSnackbar(item.name ?? item.sku ?? "Item"));
			}
		} catch {
			dispatch(marketingMaterialsCartActions.setCartItems(previousCartItems));
		}
	};

	const removeItemFromCart = async (item: models["ShoppingCartItem"]) => {
		const previousCartItems = [...cartItems];
		dispatch(marketingMaterialsCartActions.removeCartItem(item));

		try {
			const updatedCart = await updateCart({ item, quantity: 0 }).unwrap();
			dispatch(marketingMaterialsCartActions.setCartItems(updatedCart?.items ?? []));
		} catch {
			dispatch(marketingMaterialsCartActions.setCartItems(previousCartItems));
		}
	};

	const getTotalPrice = () => {
		const currentCart = cartItems.length > 0 ? cartItems : confirmedItems;
		return currentCart.reduce((acc, item) => acc + (item.listPrice ?? 0) * (item.quantityOrdered ?? 0), 0);
	};

	const validateShoppingCart = async (): Promise<boolean> => {
		if (!selectedMerchAccount?.accountId) {
			dispatch(marketingMaterialsCartActions.setMerchAccountSelectionError(true));
			return false;
		}

		dispatch(marketingMaterialsCartActions.setMerchAccountSelectionError(false));
		dispatch(marketingMaterialsCartActions.clearValidation());

		try {
			const validationResult = await validateCart({ accountId: selectedMerchAccount?.accountId }).unwrap();

			if (!validationResult) {
				dispatch(marketingMaterialsCartActions.setValidation(createValidationFallback()));
				return false;
			}

			dispatch(marketingMaterialsCartActions.setValidation(validationResult));
			return validationResult.isValid ?? (validationResult.errors?.length ?? 0) === 0;
		} catch {
			dispatch(marketingMaterialsCartActions.setValidation(createValidationFallback()));
			return false;
		}
	};

	const setSelectedMerchAccount = useCallback(
		(account: models["CustomerAccountViewModel"] | null) => {
			dispatch(marketingMaterialsCartActions.setSelectedMerchAccount(account));
		},
		[dispatch]
	);

	const setShippingDetails = useCallback(
		(details: CartShippingDetailsState) => {
			dispatch(marketingMaterialsCartActions.setShippingDetails(details));
		},
		[dispatch]
	);
	const setCampaignItems = useCallback(
		(items: CampaignAllocation[]) => {
			dispatch(marketingMaterialsCartActions.setCampaignItems(items));
		},
		[dispatch]
	);
	const toggleCampaignSelection = useCallback(
		(key: string) => {
			dispatch(marketingMaterialsCartActions.toggleCampaignSelection(key));
		},
		[dispatch]
	);
	const clearCampaignSelection = useCallback(() => {
		dispatch(marketingMaterialsCartActions.clearCampaignSelection());
	}, [dispatch]);

	return {
		cartData: {
			cartItems,
			campaignItems,
			cartSize
		} as MarketingMaterialsCart,
		selectedMerchAccount,
		isMerchAccountSelectionError,
		setSelectedMerchAccount,
		shippingDetails,
		setShippingDetails,
		campaignItems,
		selectedCampaignKeys,
		setCampaignItems,
		toggleCampaignSelection,
		clearCampaignSelection,
		validation,
		validationErrors,
		validationInfoMessages,
		isShoppingCartLoading,
		isValidatingShoppingCart,
		validateShoppingCart,
		addCartItem,
		removeItemFromCart,
		emptyCart,
		loadCart,
		getTotalPrice,
		confirmedItems
	};
};

export default useMarketingMaterialsCart;
